import styled, { keyframes } from 'styled-components';
import React, { useEffect, useState } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";

import StarImg from 'assets/images/UI/star.svg'
import Arrow from 'assets/images/UI/arrow_blue.svg'

import SummaryChart from "./SummaryChart";
import factor_content from './factor_content.json';
import factor_content_fr from './factor_content_fr.json';

import Anchor from "../Anchor";
import { calculate_summary_chart,calculate_longitudinal_chart } from './calculations';
import ItemModal from "./ItemModal";
import CompareAnchorsModal from "./CompareAnchorsModal";
import { usePageTranslations } from 'hooks/usePageTranslations';
import { useLanguage } from 'hooks/useLanguage';

// Temporary development constant: maximum anchors to compare at once
const MAX_ANCHOR_COMPARE = 20; // TODO: make configurable

const FactorContent = ({factor,core_data,outcomeQ,data,rule,selectedFactor,historical_data,lastData}) =>{
  const t = usePageTranslations('app/audit/simple/factor/FactorContent/translations');
  const { language } = useLanguage();

  // Select the appropriate content file based on language
  const contentData = language === 'fr' ? factor_content_fr : factor_content;

  const [selected, setSelected] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [open, setOpen] = useState(false);
  const [scoreVisible, setScoreVisible] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [selectedAnchors, setSelectedAnchors] = useState([]); // anchor option names

  const anchor = useSelector((state) => state.audit?.anchor, shallowEqual);

  useEffect(() => {
    if (data?.length == 0 || data == null) {
      return;
    }

    setScoreVisible(false); // Reset visibility when switching
    setChartData(
      calculate_summary_chart(
        data,
        lastData,
        anchor,
        selectedFactor,
        core_data,
        rule
      )
    );
    if (anchor) {
      setModalData(
        calculate_longitudinal_chart(
          historical_data,
          anchor,
          selectedFactor,
          core_data.categories[0].categories,
          core_data.questions
        )
      );
    }
  }, [selectedFactor, anchor, data]);

  // Smooth transition when chartData becomes available
  useEffect(() => {
    if (chartData?.overallStats?.average !== undefined) {
      const timer = setTimeout(() => setScoreVisible(true), 100); // Small delay for smooth transition
      return () => clearTimeout(timer);
    }
  }, [chartData?.overallStats?.average]);

  useEffect(() => {
    if (selected) {
      setOpen(true);
    }
  }, [selected]);

  // Reset selected anchors when parent anchor changes
  useEffect(() => {
    setSelectedAnchors([]);
  }, [anchor]);

  // Do not auto-open comparison modal; user will open via button in UI

  const close = () => {
    setOpen(false);
    setSelected(null);
  };

  const getChange = () => {
    if (chartData?.lastOverallStats.average) {
      let change =
        chartData?.overallStats.average - chartData?.lastOverallStats.average;
      return (change * 10).toFixed(1) + "%";
    }
    return "--";
  };

  const getOverallScore = () => {
    if (!chartData?.overallStats?.average && chartData?.overallStats?.average !== 0) {
      return <LoadingDots>•••</LoadingDots>; // Show animated placeholder while loading
    }
    
    return (
      <ScoreTransition visible={scoreVisible}>
        {(chartData.overallStats.average * 10).toFixed(0)}%
      </ScoreTransition>
    );
  };

  const findContent = () => {
    let content =
      selectedFactor.title in contentData
        ? contentData[selectedFactor.title]
        : null;

    if (content) {
      return (
        <>
          <p>{content.content.p}</p>
        </>
      );
    }
    return "";
  };

  // Build filtered chart data if user selected anchors
  const buildFilteredChartData = (base, names) => {
    if (!base || !Array.isArray(base.categories) || !names?.length) return base;
    const nameSet = new Set(names);
    const indices = base.categories
      .map((n, idx) => (nameSet.has(n) ? idx : -1))
      .filter((i) => i >= 0);
    if (indices.length === 0) return { ...base, categories: [], series: base.series.map(s => ({ ...s, data: [] })) };

    const pick = (arr) => indices.map((i) => arr[i]);

    const newSeries = (base.series || []).map((s) => ({ ...s, data: pick(s.data || []) }));
    const newLastSeries = (base.lastSeries || []).map((s) => ({ ...s, data: pick(s.data || []) }));

    const newQuestionSeries = {};
    Object.keys(base.questionSeries || {}).forEach((q) => {
      newQuestionSeries[q] = {};
      Object.keys(base.questionSeries[q]).forEach((metric) => {
        newQuestionSeries[q][metric] = {
          name: base.questionSeries[q][metric].name,
          data: pick(base.questionSeries[q][metric].data || [])
        };
      });
    });

    const newQuestionLastSeries = {};
    Object.keys(base.questionLastSeries || {}).forEach((q) => {
      newQuestionLastSeries[q] = {};
      Object.keys(base.questionLastSeries[q]).forEach((metric) => {
        newQuestionLastSeries[q][metric] = {
          name: base.questionLastSeries[q][metric].name,
          data: pick(base.questionLastSeries[q][metric].data || [])
        };
      });
    });

    return {
      ...base,
      categories: pick(base.categories),
      series: newSeries,
      lastSeries: newLastSeries,
      questionSeries: newQuestionSeries,
      questionLastSeries: newQuestionLastSeries,
    };
  };

  const anchorCategory = core_data?.categories?.[0]?.categories?.find((c) => c.id === anchor);
  const anchorOptionNames = anchorCategory?.options?.map((o) => o.name) || [];
  const tooManyAnchors = (anchorCategory?.options?.length || 0) > MAX_ANCHOR_COMPARE;
  const effectiveChartData = selectedAnchors.length > 0 ? buildFilteredChartData(chartData, selectedAnchors) : chartData;

  const applySelection = (names) => {
    setSelectedAnchors(names);
  };

  const clearSelection = () => {
    setSelectedAnchors([]);
  };

  // Check if there's insufficient data based on the rule (render inline, do not early return)
  const minimumResponses = core_data?.Whitelabel?.rule || 2;
  
  // Count unique respondents in the filtered data
  const countUniqueRespondents = () => {
    if (!data || !Array.isArray(data)) return 0;
    const uniqueEmployees = new Set();
    data.forEach((response) => {
      // Handle both direct response objects and potentially nested structures
      const employee = response?.employee || response?.data?.employee;
      if (employee) {
        uniqueEmployees.add(employee);
      }
    });
    return uniqueEmployees.size;
  };
  
  const uniqueRespondentCount = countUniqueRespondents();
  
  // Check if data is insufficient: either no categories OR total respondents don't meet rule
  const insufficientData = 
    (chartData && chartData.categories && chartData.categories.length === 0) ||
    uniqueRespondentCount <= minimumResponses;

  return (
    <Container>
      <Title>{factor?.title}</Title>

      <Content>
        {!insufficientData && (
          <div style={{ display: "flex" }}>
            <ScoreArea>
              <GreenBlock>
                <ScoreImg src={StarImg} />
              </GreenBlock>

              <div>
                <Score>{getOverallScore()}</Score>
                <ScoreTitle>{t('overallScore')}</ScoreTitle>
              </div>
            </ScoreArea>

            <ScoreArea>
              <BlueBlock>
                <ScoreImg2 src={Arrow} />
              </BlueBlock>

              <div>
                <Score>{getChange()}</Score>
                <ScoreTitle>{t('change')}</ScoreTitle>
              </div>
            </ScoreArea>
          </div>
        )}

        <Anchor anchorLabel=" " />
      </Content>

      <Description>{findContent()}</Description>

      {tooManyAnchors && (
        <ControlBar>
          {selectedAnchors.length === 0 ? (
            <>
              <InfoText>{t('pleaseSelectTagsToCompare', { max: MAX_ANCHOR_COMPARE })}</InfoText>
              <ControlButton onClick={() => setCompareOpen(true)}>{t('selectTags')}</ControlButton>
            </>
          ) : (
            <>
              <Chips>
                {selectedAnchors.map((n) => (
                  <Chip key={n}>{n}</Chip>
                ))}
              </Chips>
                <ControlButton onClick={() => setCompareOpen(true)}>{t('editTags')}</ControlButton>
                <ControlButtonBasic onClick={clearSelection}>{t('clear')}</ControlButtonBasic>
            </>
          )}
        </ControlBar>
      )}

      {/* <Tabs>
                <NavContainer 
                    selected={selected==1} 
                    onClick={()=>setSelected(1)}>Summary</NavContainer>
                <NavContainer
                    selected={selected==2} 
                    onClick={()=>setSelected(2)}>Trends</NavContainer>
                <NavContainer
                    selected={selected==3} 
                    onClick={()=>setSelected(3)}>Questions</NavContainer>
            </Tabs> */}

      {!insufficientData && (!tooManyAnchors || selectedAnchors.length > 0) && (
        <SummaryChart
          chartData={effectiveChartData}
          data={data}
          anchor={anchor}
          core_data={core_data}
          setSelected={setSelected}
          selectedFactor={selectedFactor}
        />
      )}

      {insufficientData && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '300px',
          textAlign: 'center',
          color: '#666',
          fontFamily: 'Raleway, sans-serif'
        }}>
          <div>
            <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              {t('insufficientResponseData')}
            </div>
            <div style={{ fontSize: '14px' }}>
              {t('factorRequiresMoreResponses', { count: minimumResponses })}
            </div>
          </div>
        </div>
      )}

      {!insufficientData && selected && (
        <ItemModal
          open={open}
          setOpen={close}
          data={modalData}
          selected={selected}
          core_data={core_data}
        />
      )}

      <CompareAnchorsModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        onApply={applySelection}
        onClear={clearSelection}
        options={anchorOptionNames}
        maxSelectable={MAX_ANCHOR_COMPARE}
        initialSelected={selectedAnchors}
      />
    </Container>
  );
}

export default FactorContent;


const Container = styled.div`

`

const Title = styled.div`
    color:#2A3039;
    font-weight:600;
    font-size:18px;
    margin-bottom:30px;
`

const T1 = styled.div`
    font-size:18px;
    font-weight:bold;
    color:#2A3039;
    margin-bottom:20px;
`

const Content = styled.div`
    display:flex;
    flex-direction:row;
    justify-content:space-between;
`

const ScoreArea = styled.div`
    display:flex;
    flex-direction:row;
    align-items:center;
    margin-right:50px;
`

const ScoreTitle = styled.div`
    color:#666D79;
    font-size:12px;

`

const Score = styled.div`
color:#2A3039;
font-size:14px;
font-weight:600;
`

const ScoreImg = styled.img`
    width:20px;
`

const ScoreImg2 = styled.img`
    width:30px;
`

const GreenBlock = styled.div`
    background-color:#EAFBF7;
    height:40px;
    width:40px;
    border-radius:8px;
    display:flex;
    justify-content:center;
    align-items:center;
    margin-right:10px;
`

const BlueBlock = styled.div`
     background-color:#EBF1FD;
    height:40px;
    width:40px;
    border-radius:8px;
    display:flex;
    justify-content:center;
    align-items:center;
    margin-right:10px;
`

const Description = styled.div`
    margin-top:20px;
`

const ControlBar = styled.div`
    margin-top: 16px;
    display:flex;
    align-items:center;
    gap: 12px;
`

const InfoText = styled.div`
    color:#666D79;
    font-size:12px;
`

const Chips = styled.div`
    display:flex;
    flex-wrap: wrap;
    gap:8px;
`

const Chip = styled.div`
    padding:6px 10px;
    border:1px solid #DFDFDF;
    border-radius:6px;
    font-size:12px;
    color:#2A3039;
`

const ControlButton = styled.button`
    background-color:#2D70E2;
    color:white;
    border:none;
    border-radius:6px;
    padding:8px 12px;
    font-size:12px;
    cursor:pointer;
`

const ControlButtonBasic = styled.button`
    background-color:white;
    color:#2A3039;
    border:1px solid #DFDFDF;
    border-radius:6px;
    padding:8px 12px;
    font-size:12px;
    cursor:pointer;
`

const NavContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 100px;
	padding: 20px 0px 5px 0px;
	color: ${props=>props.selected?'#2D70E2':'#666D79'};
	border-bottom:${props=>props.selected?'2px solid #2D70E2':""};
    font-weight:${props=>props.selected?'600':'400'};
	margin-bottom:-1px;

	cursor: pointer;
`;

const Tabs = styled.div`
	display: flex;
`

const LoadingDots = styled.div`
    color: #C7CFDB;
    font-size: 14px;
    font-weight: 600;
    animation: pulse 1.5s ease-in-out infinite;
    
    @keyframes pulse {
        0%, 100% { opacity: 0.4; }
        50% { opacity: 1; }
    }
`

const ScoreTransition = styled.div`
    opacity: ${props => props.visible ? 1 : 0};
    transform: translateY(${props => props.visible ? '0' : '4px'});
    transition: all 0.4s ease-out;
    color: #2A3039;
    font-size: 14px;
    font-weight: 600;
`